// Account-level user settings — the provider and its hook.
//
// Holds the single `user_settings` preference row for the signed-in user
// and hands it out synchronously, so every consumer reads a setting
// without its own fetch and without a first-paint flicker.
//
// Read path (three layers, best available wins):
//   1. Account — the fetched row. Canonical; the whole point of this.
//   2. Browser cache — the last-known blob for THIS user, so the first
//      paint after a reload is already correct rather than flashing the
//      default while the fetch lands.
//   3. Baseline — per descriptor.
//
// Write path: update state optimistically (the UI must respond to a
// click instantly), write the browser cache, and debounce a PUT of the
// merged blob. Debounced because a drag-reorder fires a write per
// pointer move; one save per gesture, not thirty.
//
// If the save fails, the value is KEPT rather than reverted, and the
// browser cache retains it. The setting then behaves exactly as it did
// before this system existed — correct on this browser, not yet synced —
// which is a better failure than yanking a folder back under the
// user's cursor. It's logged, not toasted: a background preference save
// is not something to interrupt someone over.
//
// Not mounted / signed out (public routes — sign-in, intake, upload):
// the context falls back to a browser-only implementation. Nothing on
// those routes reads a setting, but degrading beats throwing — a missing
// provider must never be able to blank a page.

import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";

import { api } from "../api";
import { useSession } from "../session";
import { UserSettingsContext, type UserSettingsContextValue } from "./user-settings-context";
import {
  adoptLegacySettings,
  parseUserSettingsBlob,
  USER_SETTINGS_SURFACE_KEY,
  writeSetting,
  type UserSettingDescriptor,
  type UserSettingsBlob,
} from "./user-settings-registry";

// The consumer hook lives in ./user-settings-context (no JSX there, so
// pure modules can import it). Re-exported here for discoverability.
export { useUserSetting } from "./user-settings-context";

// Debounce for the PUT. Long enough to collapse a drag gesture, short
// enough that a user who changes a setting and immediately closes the
// tab almost always gets it saved.
const SAVE_DEBOUNCE_MS = 400;

// Cache key is per-user: two people signing into the same browser (or an
// admin impersonating) must never inherit each other's layout.
function cacheKey(userId: string): string {
  return `stewardship-os.user-settings.cache.v1:${userId}`;
}

interface BrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  // Enumeration, for the settings whose old storage was a FAMILY of keys
  // rather than one (book scope wrote one per list entity type). Typed
  // optional because a stubbed storage in tests may not implement them.
  readonly length?: number;
  key?(index: number): string | null;
}
function getStorage(): BrowserStorage | null {
  const g = globalThis as unknown as { localStorage?: BrowserStorage };
  try {
    return g.localStorage ?? null;
  } catch {
    // Some privacy modes throw on property access, not just on write.
    return null;
  }
}

/** Every key currently in localStorage. Used only by the one-time
 *  legacy-adoption sweep, for settings that were stored as a family of
 *  keys. Returns [] rather than throwing on a storage that blocks
 *  enumeration — adoption then simply finds nothing. */
function listStorageKeys(storage: BrowserStorage | null): string[] {
  if (!storage || typeof storage.key !== "function") return [];
  try {
    const total = storage.length ?? 0;
    const out: string[] = [];
    for (let i = 0; i < total; i++) {
      const key = storage.key(i);
      if (typeof key === "string") out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function readCache(userId: string): UserSettingsBlob | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(cacheKey(userId));
    return raw ? parseUserSettingsBlob(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(userId: string, blob: UserSettingsBlob): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(cacheKey(userId), JSON.stringify(blob));
  } catch {
    // Quota / SecurityError — the account copy is canonical anyway.
  }
}

export function UserSettingsProvider({ children }: PropsWithChildren) {
  const { status, user, impersonation } = useSession();
  const userId = status === "signed_in" ? (user?.id ?? null) : null;
  // Adoption reads THIS BROWSER's leftover localStorage and writes it to
  // whoever `userId` currently is. While impersonating, that is somebody
  // else's account — so an admin opening a teammate's view would silently
  // overwrite that teammate's saved settings with their own machine's,
  // permanently, with no action taken and nothing said.
  //
  // Found in the 2026-08-29 staging audit exactly this way: an auditor's
  // leftover Insurance lens became Ryan's stored default, and he is not
  // an Insurance advisor. Adoption is a one-time migration for a person
  // sitting at their own browser; it has no business running for a
  // session where the browser and the account belong to two people.
  const isImpersonating = impersonation !== null;

  const [blob, setBlob] = useState<UserSettingsBlob>({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Latest blob, for the debounced save to read without being recreated
  // on every state change (which would reset the timer forever).
  const blobRef = useRef<UserSettingsBlob>(blob);
  blobRef.current = blob;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load: seed from this browser's cache for an immediate correct paint,
  // then reconcile with the account and run the legacy-adoption sweep.
  useEffect(() => {
    if (!userId) {
      setBlob({});
      setIsLoaded(false);
      return;
    }
    let cancelled = false;

    const cached = readCache(userId);
    if (cached) setBlob(cached);

    api
      .getViewPreference(USER_SETTINGS_SURFACE_KEY)
      .then((resolved) => {
        if (cancelled) return;
        const fromServer = resolved.preferences_json
          ? (parseUserSettingsBlob(resolved.preferences_json) ?? {})
          : {};
        const storage = isImpersonating ? null : getStorage();
        const adopted = adoptLegacySettings(
          fromServer,
          (key) => (storage ? storage.getItem(key) : null),
          () => listStorageKeys(storage),
        );
        setBlob(adopted);
        writeCache(userId, adopted);
        setIsLoaded(true);
        // Anything adopted exists only on this browser so far — push it
        // up so the next device inherits it.
        if (Object.keys(adopted).length > Object.keys(fromServer).length) {
          void api
            .setViewPreference(USER_SETTINGS_SURFACE_KEY, JSON.stringify(adopted))
            .catch(() => {
              // Next change to any setting re-sends the whole blob.
            });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Offline / 5xx — run on the cached blob. Every setting keeps
        // working on this browser; it just isn't syncing right now.
        setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, isImpersonating]);

  // Flush a pending save on unmount so a setting changed immediately
  // before navigating away isn't dropped.
  useEffect(() => {
    return () => {
      if (saveTimer.current === null) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      if (!userId) return;
      void api
        .setViewPreference(USER_SETTINGS_SURFACE_KEY, JSON.stringify(blobRef.current))
        .catch(() => {});
    };
  }, [userId]);

  const setSetting = useCallback(
    <T,>(descriptor: UserSettingDescriptor<T>, value: T) => {
      const next = writeSetting(blobRef.current, descriptor, value);
      blobRef.current = next;
      setBlob(next);
      if (!userId) return;
      writeCache(userId, next);

      if (saveTimer.current !== null) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        void api
          .setViewPreference(USER_SETTINGS_SURFACE_KEY, JSON.stringify(blobRef.current))
          .catch((err: unknown) => {
            // Kept, not reverted — see the header note. The browser
            // cache still holds it, so the user's screen stays right.
            console.warn(
              `user-settings: couldn't save "${descriptor.id}" to your account; it's saved on this browser only.`,
              err,
            );
          });
      }, SAVE_DEBOUNCE_MS);
    },
    [userId],
  );

  const value = useMemo<UserSettingsContextValue>(
    () => ({ blob, setSetting, isLoaded }),
    [blob, setSetting, isLoaded],
  );

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}
